import { useCallback, useEffect, useRef, useState } from "react"

import {
  Box,
  Button,
  Center,
  Checkbox,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react"

import { useAppSelector } from "app/hooks"
import CustomModal from "components/custom-modal/custom-modal"
import useToastHook from "hooks/useToastHook/useToastHook"
import { apiClient } from "services/client"
import { selectCompanyTags, selectCurrentPmc } from "store/auth/authSlice"

const KNOWN_ABBREVIATIONS = new Set(["PM", "PMS", "PMC", "DNM", "TA"])

// Utility: convert camelCase to "Title Case With Spaces"
export function camelCaseToDisplayText(key: string): string {
  return (
    key
      // insert space before a single uppercase letter that follows a lowercase letter
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      // insert space before an uppercase letter that is followed by lowercase (handles "PMTest" -> "PM Test")
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .trim()
      // capitalise the first letter of each word, lowercase the rest
      .replace(/\b\w+/g, (word) => {
        const upper = word.toUpperCase()
        return KNOWN_ABBREVIATIONS.has(upper)
          ? upper
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      })
  )
}

function diffCompanyTags(
  prev: Record<string, boolean>,
  next: Record<string, boolean>,
): Record<string, boolean> {
  return Object.keys(next).reduce<Record<string, boolean>>(
    (acc, key) =>
      prev[key] !== next[key] ? { ...acc, [key]: next[key] } : acc,
    {},
  )
}

const AddTagModal = (props: {
  getCompany: () => void
  tagOrder: string[]
  isLoadingTags: boolean
}) => {
  const { getCompany, tagOrder, isLoadingTags } = props
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()
  const displayToast = useToastHook()
  const companyTagData = useAppSelector(selectCompanyTags)
  const currentPmc = useAppSelector(selectCurrentPmc)
  const toastIdRef = useRef<number | string>("")
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({})

  // Sync checkedItems whenever the stored tag data or available tags change
  useEffect(() => {
    if (!companyTagData || tagOrder.length === 0) return

    const tagDataMap = companyTagData as unknown as Record<string, boolean>
    const syncedItems = tagOrder.reduce<Record<string, boolean>>(
      (acc, key) => ({
        ...acc,
        [key]: tagDataMap[key] ?? false,
      }),
      {},
    )
    setCheckedItems(syncedItems)
  }, [companyTagData, tagOrder, currentPmc?.pmc_id])

  const addingCustomTag = useCallback(
    (companyTag: Record<string, boolean>) => {
      toastIdRef.current = toast({
        description: "Company Tag Change Successfully",
        status: "loading",
        position: "top-right",
        duration: 60 * 3 * 1000,
      })

      const newValues = {
        pmcId: currentPmc?.pmc_id,
        companyCustomTags: diffCompanyTags(
          (companyTagData as unknown as Record<string, boolean>) ?? {},
          companyTag,
        ),
      }
      apiClient
        .addingCustomTag({
          data: newValues,
        })
        .then(() => {
          getCompany()
          if (toastIdRef.current) {
            toast.update(toastIdRef.current, {
              description: "Company Tag Change Successfully",
              status: "success",
              position: "top-right",
              duration: 3000,
            })
          }
          return true
        })
        .catch(() => {
          if (toastIdRef.current) {
            toast.update(toastIdRef.current, {
              description: `Faild to add tag to the Properties`,
              status: "error",
              position: "top-right",
              duration: 3000,
            })
          }
          displayToast({
            message: "Something went wrong! Can not add tag to the property.",
          })
        })
    },
    [currentPmc?.pmc_id, displayToast, getCompany, toast],
  )

  const onSubmit = async () => {
    addingCustomTag(checkedItems)
    onClose()
  }

  const handleCheckboxChange = (key: string) => {
    setCheckedItems((prevState) => ({
      ...prevState,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      [key]: !prevState[key],
    }))
  }

  const renderTagsContent = () => {
    if (isLoadingTags) {
      return (
        <Center p={5}>
          <Spinner />
        </Center>
      )
    }
    if (tagOrder.length === 0) {
      return (
        <Text textAlign='center' color='gray.500' py={8} fontSize='sm'>
          No tags available.
        </Text>
      )
    }

    return (
      <>
        {tagOrder.map((key) => (
          <Box key={key} display='flex' marginBottom='10px'>
            <Checkbox
              isChecked={!!checkedItems[key]}
              onChange={() => handleCheckboxChange(key)}
              marginRight='10px'
            />
            <Text>{camelCaseToDisplayText(key)}</Text>
          </Box>
        ))}
      </>
    )
  }

  return (
    <>
      <Button
        variant='secondary'
        onClick={() => {
          onOpen()
        }}
        h='30px'
        px='12px'
      >
        Add Tags
      </Button>

      <CustomModal isOpen={isOpen} onClose={onClose} width='400px' maxH='650px'>
        <Box>
          <Text
            size={{
              base: "lg",
              md: "lg2",
            }}
            fontWeight={{
              base: 600,
              md: 700,
            }}
            variant='black'
            marginBottom='10px'
          >
            Company Tags
          </Text>
          <Box p='10px'>{renderTagsContent()}</Box>
          <Box
            px='10px'
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Button
              width='100%'
              height='50px'
              marginY={{ base: "0px", sm: "16px" }}
              type='submit'
              isDisabled={isLoadingTags || tagOrder?.length === 0}
              onClick={() => onSubmit()}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </CustomModal>
    </>
  )
}

export default AddTagModal
